import React, { useState } from 'react';
import { ArrowRight, Calendar, User } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useNavigate } from 'react-router-dom';

import SEO from '../components/SEO';
import FadeIn from '../components/FadeIn';
import { useTranslation } from 'react-i18next';

const Blog: React.FC = () => {
  const navigate = useNavigate();
  const { blogPosts } = useData();
  const featuredPost = blogPosts[0];
  const regularPosts = blogPosts.slice(1);
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 9;
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentRegularPosts = regularPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(regularPosts.length / postsPerPage);

  return (
    <div className="w-full bg-brand-light">
      <SEO
        pageCode="blog"
        title="Blog & Haberler"
        description="Teknoloji dünyasından haberler, yazılım trendleri ve Pato Yazılım'dan güncel bilgiler."
        link="/blog"
      />
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-brand-dark text-center">
        <FadeIn className="container mx-auto px-4 md:px-8">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {t('blog.heroTitle')}
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {t('blog.heroDesc')}
          </p>
        </FadeIn>
      </section>

      <div className="container mx-auto px-4 md:px-8 py-16">
        {/* Featured Post */}
        {currentPage === 1 && (
          <FadeIn className="mb-16">
            <div
              onClick={() => navigate(`/blog/${featuredPost.slug}`)}
              className="group relative rounded-3xl overflow-hidden shadow-2xl bg-white grid lg:grid-cols-2 cursor-pointer"
            >
              <div className="relative h-56 lg:h-auto overflow-hidden">
                <img
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-4 left-4 bg-brand-blue text-white px-4 py-1 rounded-full text-sm font-medium shadow-lg">
                  {t('common.featured')}
                </div>
              </div>
              <div className="p-6 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  <span className="flex items-center gap-1"><Calendar size={16} /> {featuredPost.date}</span>
                  <span className="text-brand-blue font-medium">{featuredPost.category}</span>
                </div>
                <h2 className="text-xl md:text-3xl font-bold text-brand-dark mb-4 group-hover:text-brand-blue transition-colors">
                  {featuredPost.title}
                </h2>
                <p className="text-gray-700 text-sm md:text-lg mb-8 line-clamp-3">
                  {featuredPost.excerpt}
                </p>
                <button
                  onClick={() => navigate(`/blog/${featuredPost.slug}`)}
                  className="w-full lg:w-auto py-2 lg:py-0 rounded-lg lg:rounded-none border lg:border-none border-brand-blue/20 lg:border-transparent text-brand-blue font-medium lg:font-bold hover:bg-brand-blue lg:hover:bg-transparent hover:text-white lg:hover:text-brand-blue transition-all flex items-center justify-center lg:justify-start gap-2 lg:hover:gap-3"
                >
                  {t('common.readMore')} <ArrowRight size={16} className="lg:w-5 lg:h-5" />
                </button>
              </div>
            </div>
          </FadeIn>
        )}

        {/* Grid Posts */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {currentRegularPosts.map((post, idx) => (
            <FadeIn key={post.id} delay={idx * 0.1}>
              <article
                onClick={() => navigate(`/blog/${post.slug}`)}
                className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 flex flex-col h-full group cursor-pointer"
              >
                <div className="h-56 overflow-hidden relative">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-brand-dark px-3 py-1 rounded-full text-xs font-bold">
                    {post.category}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                    <span className="flex items-center gap-1"><Calendar size={14} /> {post.date}</span>
                  </div>
                  <h3 className="text-xl font-bold text-brand-dark mb-3 line-clamp-2 group-hover:text-brand-blue transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-700 text-sm mb-6 line-clamp-3 flex-grow">
                    {post.excerpt}
                  </p>
                  <button
                    onClick={() => navigate(`/blog/${post.slug}`)}
                    className="w-full py-2 rounded-lg border border-brand-blue/20 text-brand-blue font-medium hover:bg-brand-blue hover:text-white transition-colors flex items-center justify-center gap-2"
                  >
                    {t('common.readMore')} <ArrowRight size={16} />
                  </button>
                </div>
              </article>
            </FadeIn>
          ))}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-12">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setCurrentPage(i + 1);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors font-medium shadow-sm border ${
                  currentPage === i + 1 
                    ? 'bg-brand-blue text-white border-brand-blue' 
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;